import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import React, { useState } from 'react';
import {
  useCreateEpdScrapInput,
  useCreateEpdShareOfTotalScrapInput,
  useDeleteEpdScrapInputs,
  useUpdateEpdScrapInput,
  useUpdateEpdShareOfTotalScrapInput,
} from 'services/api/mutations';
import { useGetEpdScrapInputs, useGetEpdShareOfTotalScrapInput } from 'services/api/queries';
import { ScrapInputModel } from 'types/types';

import useIsReadOnlyMode from '../../hooks/useIsReadOnlyMode';
import DynamicGridHeader from '../DynamicGridHeader';
import { cellTextEditor } from '../GridCellEditors';
import { numericCellBody, textCellBody } from '../GridService';
import { ColumnFooterInputNumber, DataTableStyled } from '../StyledGrid';
import { validatedCellTemplate } from '../GridService';
import useDynamicGridWithEmptyFirstRow from '../useDynamicGridWithEmptyFirstRow';
import useGridRowSelectionViaCheckbox from '../useGridRowSelectionViaCheckbox';

const ContributionOfScrapInputsGrid: React.FunctionComponent<{
  epdVersionId: string;
  epdShareOfTotalScrapInputError?: any;
  required?: any;
}> = ({ epdVersionId, epdShareOfTotalScrapInputError, required }) => {
  const [isFooterEditing, setIsFooterEditing] = useState(false);
  const isReadOnly = useIsReadOnlyMode();

  const shareOfTotalScrapInput = useGetEpdShareOfTotalScrapInput(epdVersionId).data;
  const createShareOfTotalScrapInput = useCreateEpdShareOfTotalScrapInput(epdVersionId);
  const updateShareOfTotalScrapInput = useUpdateEpdShareOfTotalScrapInput(epdVersionId);

  const rows = useGetEpdScrapInputs(epdVersionId!).data;
  const createScrapInputMutation = useCreateEpdScrapInput(epdVersionId);
  const updateScrapInputMutation = useUpdateEpdScrapInput(epdVersionId);
  const deleteMutation = useDeleteEpdScrapInputs(epdVersionId);

  const { dataTableProps, checkboxColumnNode, handleDeleteSelected, isDeleteDisabled, gridId } =
    useGridRowSelectionViaCheckbox<ScrapInputModel>(isReadOnly, deleteMutation);

  const rowClassName = () => ({ 'p-disabled': isReadOnly });

  const { gridData, handleAddRow, handleCellEdit } = useDynamicGridWithEmptyFirstRow<ScrapInputModel>({
    rows,
    createMutation: createScrapInputMutation,
    updateMutation: updateScrapInputMutation,
    emptyEntity: { epdVersionId, name: '', value: undefined },
  });

  const isTableFilledIn = (rows?.every((r) => r.name) && rows?.every((r) => r.value)) || !required;

  const header = (
    <DynamicGridHeader
      hasValue={isTableFilledIn}
      onAddRow={handleAddRow}
      onDeleteRows={handleDeleteSelected}
      deleteRowsDisabled={isDeleteDisabled}
    />
  );

  const onFooterCellEditComplete = (e: any) => {
    const value = e.target.value?.replace(' %', '');
    if (!shareOfTotalScrapInput?.id) {
      createShareOfTotalScrapInput.mutate({ epdVersionId, sharePercent: value ?? null });
    } else {
      updateShareOfTotalScrapInput.mutate({ ...shareOfTotalScrapInput, sharePercent: value ?? null });
    }
    setIsFooterEditing(false);
  };

  const renderTotalCellTemplate = () =>
    validatedCellTemplate(
      shareOfTotalScrapInput?.sharePercent !== null && shareOfTotalScrapInput?.sharePercent !== undefined
        ? `${shareOfTotalScrapInput?.sharePercent}%`
        : null,
      epdShareOfTotalScrapInputError?.scrapInputsTotal?.scrapInputTotalPercent,
      'Type value %'
    );

  const footerNumberEditor = () => (
    <ColumnFooterInputNumber
      placeholder="Type value %"
      value={shareOfTotalScrapInput?.sharePercent}
      onBlur={onFooterCellEditComplete}
      mode="decimal"
      step={0.01}
      maxFractionDigits={2}
      disabled={isReadOnly}
      autoFocus
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
          setIsFooterEditing(false);
        }
      }}
    />
  );

  const footerGroup = (
    <ColumnGroup>
      <Row style={{ borderTop: 'none' }}>
        <Column className="p-row-note" />
        <Column
          footer={<div>The share of the total scrap input that was assumed to come with an environmental burden</div>}
          className="p-row-note"
        />
        <Column
          footer={
            isFooterEditing && !isReadOnly ? (
              <div onClick={(e) => e.stopPropagation()}>{footerNumberEditor()}</div>
            ) : (
              <div onClick={() => !isReadOnly && setIsFooterEditing(true)}>{renderTotalCellTemplate()}</div>
            )
          }
          colSpan={4}
          className={isReadOnly ? 'p-row-note' : undefined}
        />
      </Row>
    </ColumnGroup>
  );

  return (
    <DataTableStyled
      key={gridId}
      dataKey="ord"
      header={header}
      footerColumnGroup={footerGroup}
      value={gridData}
      editMode="cell"
      rowClassName={rowClassName}
      showGridlines
      tableStyle={{ tableLayout: 'fixed' }}
      {...dataTableProps}
    >
      {checkboxColumnNode}
      <Column
        field="name"
        header="Material scrap name"
        editor={isReadOnly ? undefined : cellTextEditor}
        onCellEditComplete={handleCellEdit}
        body={textCellBody({ placeholder: 'Type name' })}
        style={{ width: '70%' }}
      />
      <Column
        field="value"
        header="Material scrap value"
        body={numericCellBody({
          placeholder: (
            <>
              Type value, kg CO<sub>2</sub> eq./tonne
            </>
          ),
          format: (value) => (
            <>
              {value}, kg CO<sub>2</sub> eq./tonne
            </>
          ),
        })}
        editor={isReadOnly ? undefined : cellTextEditor}
        onCellEditComplete={handleCellEdit}
        style={{ width: '30%' }}
      />
    </DataTableStyled>
  );
};

export default ContributionOfScrapInputsGrid;
